import { Type } from '@angular/core';
// Re-export from the pure module so consumers can keep importing from this
// file. See `role-filter.ts` for the pure helpers (no Angular deps so they
// can be unit-tested in isolation).
export { RoleTier, ROLE_RANK, passesRoleGate, filterRoutesByRole } from './role-filter';
import { RoleTier } from './role-filter';

/**
 * Marketing Workspace section registry.
 *
 * URL pattern: ?section=<path>  (e.g. ?section=strategy/doc, ?section=programs/blog)
 * In-page anchors (Strategy doc TOC scroll-spy) use #<id> separately and never collide.
 *
 * Adding a new section:
 *   1. Create a small Angular component (e.g. ExperimentsProposeSection) that renders the surface
 *   2. Add an entry below with path/parent/label/component
 *   3. Declare the component in ContentPipelineUIModule
 *
 * The shell uses *ngComponentOutlet keyed off `path`, so the shell does not need editing.
 */
export interface StubContent {
    eyebrow: string;
    title: string;
    sub: string;
    csTitle: string;
    csBody: string;
    csIcon?: string;
    legacyLabel?: string;
    legacyHref?: string;
}

export interface WorkspaceRoute {
    /** URL-style path; what `?section=` carries. e.g. 'strategy/doc' */
    path: string;
    /** Top-tab key — null means top-level only (e.g. 'home'). */
    parent: WorkspaceTab | null;
    /** Human label rendered in nav. */
    label: string;
    /** Component to render in the main content area. */
    component: Type<unknown>;
    /** Optional icon (Font Awesome class) for tile/sub-nav rendering. */
    icon?: string;
    /** Optional phase tag for nav pills ('Phase 1' = live, 'Phase 2'/'Phase 3' = preview/coming). */
    phase?: 'Phase 1' | 'Phase 2' | 'Phase 3';
    /** If true, this entry shows on the parent's sub-nav strip. */
    showInSubnav?: boolean;
    /** Optional stub content; passed as inputs when component is StubSection. */
    stub?: StubContent;
    /**
     * Minimum role required to see this route. Undefined = any authorized
     * user can see it. Set to 'Operator' for routes that perform writes,
     * 'Admin' for strategy / governance / permission-management routes.
     * The shell filters sub-nav with `filterRoutesByRole`; deep-link access
     * is enforced separately by the shell's `setSection` guard. Server-side
     * enforcement lives on the action layer via MarketingBaseAction.
     */
    requiredRole?: RoleTier;
}

export type WorkspaceTab =
    | 'strategy'
    | 'programs'
    | 'experiments'
    | 'analytics'
    | 'foundation'
    | 'admin';

/**
 * Top-level tabs. `requiredRole` mirrors the highest-rank gate among the
 * tab's child routes so the top-nav itself can be hidden for under-privileged
 * users (e.g. Admin-only Admin tab).
 */
export const WORKSPACE_TABS: { key: WorkspaceTab; label: string; defaultPath: string; requiredRole?: RoleTier }[] = [
    { key: 'strategy', label: 'Strategy', defaultPath: 'strategy/doc' },
    { key: 'programs', label: 'Programs', defaultPath: 'programs' },
    { key: 'experiments', label: 'Experiments', defaultPath: 'experiments' },
    { key: 'analytics', label: 'Analytics', defaultPath: 'analytics' },
    { key: 'foundation', label: 'Foundation', defaultPath: 'foundation' },
    { key: 'admin', label: 'Admin', defaultPath: 'admin/users-and-roles', requiredRole: 'Admin' },
];

/**
 * Registry instance built once at module load. Source of truth.
 * `WorkspaceRoutesProvider` (below) returns this so the shell + sub-nav builders can consume it.
 */
let routes: WorkspaceRoute[] = [];

export function registerWorkspaceRoutes(entries: WorkspaceRoute[]): void {
    routes = [...routes, ...entries];
}

export function getWorkspaceRoutes(): WorkspaceRoute[] {
    return routes;
}

export function findRoute(path: string): WorkspaceRoute | null {
    return routes.find(r => r.path === path) ?? null;
}

export function getSubnavRoutes(parent: WorkspaceTab): WorkspaceRoute[] {
    return routes.filter(r => r.parent === parent && r.showInSubnav !== false);
}

// passesRoleGate / filterRoutesByRole are re-exported from ./role-filter at
// the top of this file. Pure-function tests live in
// packages/Marketing/src/__tests__/utils/uiRoleFilter.test.ts.
